import { Component, EventEmitter, Input, Output, OnChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../button/button.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { SkillExtended } from '../../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { SelectComponent } from '../../select/select.component';
import { CvOptions } from '../../../models/cv-options.model';
import { FIELD_VALIDATION, VALIDATION_VALUES } from '../../../../../../../libs/shared/src/lib/constants/validation-constants';
import { SkillSelectorComponent } from '../../../../../../../libs/shared/src/lib/skill-selector/skill-selector.component';
import { AllCVOptionsFromDB } from '../../../../../../../libs/shared/src/lib/models/cv-options.model';

@Component({
  selector: 'app-skills-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ButtonComponent, TranslocoPipe, SelectComponent, SkillSelectorComponent],
  templateUrl: './skills-form.component.html',
  styleUrls: ['./skills-form.component.scss'],
})
export class SkillsFormComponent implements OnChanges, OnInit {
  @Input() editingSkill: SkillExtended | null = null;
  @Input() editingIndex: number | null = null;
  @Input() cvOptions: CvOptions | null = null;

  @Output() save = new EventEmitter<Partial<SkillExtended>>();
  @Output() cancel = new EventEmitter<void>();

  skillForm: FormGroup;
  allCVOptionsFromDB: AllCVOptionsFromDB | null = null;
  skillLevelOptions: { value: string; label: string }[] = [];
  editingSkillCategory?: string;
  isLoading = false;

  // Expose validation constants to template
  readonly VALIDATION = FIELD_VALIDATION;

  constructor(private fb: FormBuilder, private translocoService: TranslocoService) {
    this.skillForm = this.createForm();
  }

  /**
   * Create the form with validation using SSOT validation constants
   */
  private createForm(): FormGroup {
    return this.fb.group({
      skillName: ['', [Validators.required, Validators.maxLength(FIELD_VALIDATION.SKILL_NAME_MAX_LENGTH)]],
      level: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.updateOptionsFromInput();
  }

  /**
   * Updates skill and level options from the cvOptions input
   * and prepares the options for the select components
   */
  updateOptionsFromInput(): void {
    if (this.cvOptions) {
      // Convert CvOptions format to AllCVOptionsFromDB format for skill-selector
      this.allCVOptionsFromDB = {
        skills: this.cvOptions.skills || []
      };

      // Prepare level options for select component with translations
      this.skillLevelOptions = (this.cvOptions.skillLevels || []).map((level) => ({
        value: level,
        label: this.translocoService.translate(level),
      }));
    } else {
      // Fallback to default values if no options provided
      this.allCVOptionsFromDB = { skills: [] };
      this.skillLevelOptions = [
        { value: 'beginner', label: this.translocoService.translate('beginner') },
        { value: 'intermediate', label: this.translocoService.translate('intermediate') },
        { value: 'advanced', label: this.translocoService.translate('advanced') },
        { value: 'expert', label: this.translocoService.translate('expert') },
      ];
    }
  }

  ngOnChanges(): void {
    // Update options when cvOptions input changes
    this.updateOptionsFromInput();

    // Update form values when editing skill
    if (this.editingSkill) {
      this.skillForm.patchValue({
        skillName: this.editingSkill.skillName,
        level: this.editingSkill.level,
      });

      // Set category for skill selector expansion
      if (this.allCVOptionsFromDB?.skills) {
        const skillData = this.allCVOptionsFromDB.skills.find((s) => s.skillKey === this.editingSkill!.skillName);
        if (skillData?.category) {
          this.editingSkillCategory = skillData.category;
        }
      }
    } else {
      this.skillForm.reset();
      this.editingSkillCategory = undefined;
    }
  }

  onSubmit(): void {
    if (this.skillForm.invalid) return;

    this.save.emit(this.skillForm.value);
  }

  onCancel(): void {
    this.skillForm.reset();
    this.cancel.emit();
  }
}
